import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link as RouterLink, useLocation, useNavigate, type Location } from 'react-router-dom';
import {
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import { useAuthStore } from './authStore';
import { fetchCurrentUser, login } from '../api/endpoints/auth';
import { getRecaptchaToken } from './recaptcha';
import { RecaptchaCheckbox, isRecaptchaV2Enabled } from './RecaptchaCheckbox';
import { ApiError } from '../api/types/common';
import { AuthLayout } from './AuthLayout';

const schema = z.object({
  username: z.string().min(1, 'Kullanıcı adı gerekli'),
  password: z.string().min(1, 'Şifre gerekli'),
});

type FormValues = z.infer<typeof schema>;

const REMEMBER_KEY = 'tfw:login:remembered-username';

function readRemembered(): string {
  try {
    return window.localStorage.getItem(REMEMBER_KEY) ?? '';
  } catch {
    return '';
  }
}

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const setSession = useAuthStore((s) => s.setSession);
  const setCurrentUser = useAuthStore((s) => s.setCurrentUser);
  const setAwaitingOtp = useAuthStore((s) => s.setAwaitingOtp);

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(() => !!readRemembered());
  const [recaptchaV2Token, setRecaptchaV2Token] = useState('');
  const [recaptchaResetKey, setRecaptchaResetKey] = useState(0);

  const passwordReset = (location.state as { passwordReset?: boolean } | null)?.passwordReset ?? false;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { username: readRemembered(), password: '' },
  });

  const onSubmit = async (values: FormValues) => {
    setSubmitError(null);
    try {
      let recaptchaToken: string | undefined;
      if (isRecaptchaV2Enabled()) {
        if (!recaptchaV2Token) {
          setSubmitError('Lütfen "Ben robot değilim" doğrulamasını tamamlayın.');
          return;
        }
        recaptchaToken = recaptchaV2Token;
      } else {
        recaptchaToken = await getRecaptchaToken('login');
      }

      const session = await login(values.username, values.password, recaptchaToken);

      try {
        if (remember) window.localStorage.setItem(REMEMBER_KEY, values.username);
        else window.localStorage.removeItem(REMEMBER_KEY);
      } catch {
        /* localStorage kullanılamıyorsa "beni hatırla" sessizce atlanır */
      }

      if (session.requiresOtp) {
        setAwaitingOtp({
          sessionId: session.sessionId,
          maskedEmail: session.otpMaskedEmail,
          employeeId: session.employeeID,
        });
        navigate('/verify-otp', { replace: true });
        return;
      }
      setSession(
        { sessionId: session.sessionId, sessionToken: session.sessionToken },
        session.employeeID
      );
      const currentUser = await fetchCurrentUser();
      setCurrentUser(currentUser);
      const redirectTo = (location.state as { from?: Location } | null)?.from?.pathname ?? '/';
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.message : 'Giriş yapılamadı. Lütfen tekrar deneyin.');
      // v2 token'ları tek kullanımlık — başarısız denemede kutuyu sıfırla.
      setRecaptchaV2Token('');
      setRecaptchaResetKey((k) => k + 1);
    }
  };

  return (
    <AuthLayout heading="Hoş geldiniz" subheading="TravelFuture hesabınıza giriş yapın.">
      <Stack component="form" onSubmit={handleSubmit(onSubmit)} spacing={2} noValidate>
        {passwordReset && !submitError && (
          <Alert severity="success">Parolanız güncellendi. Yeni parolanızla giriş yapabilirsiniz.</Alert>
        )}
        {submitError && <Alert severity="error">{submitError}</Alert>}

        <TextField
          label="Kullanıcı adı"
          autoComplete="username"
          autoFocus={!readRemembered()}
          fullWidth
          error={!!errors.username}
          helperText={errors.username?.message}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutlineOutlinedIcon fontSize="small" color="disabled" />
                </InputAdornment>
              ),
            },
          }}
          {...register('username')}
        />
        <TextField
          label="Şifre"
          type={showPassword ? 'text' : 'password'}
          autoComplete="current-password"
          fullWidth
          error={!!errors.password}
          helperText={errors.password?.message}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon fontSize="small" color="disabled" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? 'Şifreyi gizle' : 'Şifreyi göster'}
                    onClick={() => setShowPassword((v) => !v)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? (
                      <VisibilityOffOutlinedIcon fontSize="small" />
                    ) : (
                      <VisibilityOutlinedIcon fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
          {...register('password')}
        />

        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mt: -0.5 }}>
          <FormControlLabel
            control={
              <Checkbox size="small" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
            }
            label={<Typography variant="body2">Beni hatırla</Typography>}
          />
          <Link component={RouterLink} to="/forgot-password" variant="body2" underline="hover">
            Şifremi unuttum
          </Link>
        </Stack>

        {isRecaptchaV2Enabled() && (
          <RecaptchaCheckbox onChange={setRecaptchaV2Token} resetKey={recaptchaResetKey} />
        )}

        <Button
          type="submit"
          variant="contained"
          size="large"
          fullWidth
          disabled={isSubmitting}
          startIcon={<LoginOutlinedIcon />}
        >
          {isSubmitting ? 'Giriş yapılıyor…' : 'Giriş yap'}
        </Button>
      </Stack>
    </AuthLayout>
  );
}
